import { ChangeEvent, DragEvent, KeyboardEvent, RefObject, useRef, useState } from 'react';
import axios from 'axios';

import { TaskApi } from '../../../../../../../../api';
import NotificationManager from '../../../../../../../../common/notifications/NotificationManager';
import { MAX_ALLOWED_FILE_UPLOAD_SIZE_BYTES } from '../../../../../../../../constants/files';
import {
  FILE_SIZE_UPLOAD_EXCEPTION,
  FILE_TYPE_NOT_SUPPORTED,
  TASK_ATTACHMENT_ADD_FAILED,
  TASK_ATTACHMENT_ADDED
} from '../../../../../../../../constants/notificationMessages';

import { ACCEPTED_MIME_TYPES } from './taskAttachmentConstants';
import { AddTaskAttachmentModalProps } from './taskAttachmentTypes';

type UseAddTaskAttachmentModalResult = {
  inputRef: RefObject<HTMLInputElement | null>;
  attachment: File | null;
  isDragActive: boolean;
  isSaving: boolean;
  uploadIsActive: boolean;
  saveIsDisabled: boolean;
  handleClose: () => void;
  openFilePicker: () => void;
  handleUploadKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  handleFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleDragStart: (event: DragEvent<HTMLDivElement>) => void;
  handleDragLeave: (event: DragEvent<HTMLDivElement>) => void;
  handleDrop: (event: DragEvent<HTMLDivElement>) => void;
  removeAttachment: () => void;
  handleSave: () => Promise<void>;
};

export default function useAddTaskAttachmentModal({
  modalBox,
  taskId,
  ssuId,
  onAttachmentSaved
}: AddTaskAttachmentModalProps): UseAddTaskAttachmentModalResult {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const uploadIsActive = !attachment && !isSaving;
  const saveIsDisabled = !attachment || isSaving;

  function handleClose() {
    if (isSaving) return;
    modalBox?.close();
  }

  function openFilePicker() {
    if (!uploadIsActive) return;
    inputRef.current?.click();
  }

  function handleUploadKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    openFilePicker();
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) {
      selectFile(file);
    }
  }

  function handleDragStart(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (!uploadIsActive) return;
    setIsDragActive(true);
  }

  function handleDragLeave(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (!uploadIsActive) return;
    setIsDragActive(false);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (!uploadIsActive) return;
    setIsDragActive(false);

    const file = event.dataTransfer.files?.[0];
    if (file) {
      selectFile(file);
    }
  }

  function removeAttachment() {
    if (isSaving) return;
    setAttachment(null);
    clearFileInput();
  }

  async function handleSave() {
    if (!attachment || isSaving) return;
    setIsSaving(true);

    try {
      await TaskApi.addAttachment(taskId, ssuId, attachment);
      modalBox?.close();
      onAttachmentSaved?.();
      NotificationManager.success(TASK_ATTACHMENT_ADDED);
    } catch (error) {
      const message =
        axios.isAxiosError(error) && error.response?.status === 413
          ? FILE_SIZE_UPLOAD_EXCEPTION
          : TASK_ATTACHMENT_ADD_FAILED;
      NotificationManager.error(message);
      setIsSaving(false);
    }
  }

  function selectFile(file: File) {
    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      NotificationManager.error(FILE_TYPE_NOT_SUPPORTED);
      clearFileInput();
      return;
    }

    if (file.size > MAX_ALLOWED_FILE_UPLOAD_SIZE_BYTES) {
      NotificationManager.error(FILE_SIZE_UPLOAD_EXCEPTION);
      clearFileInput();
      return;
    }

    setAttachment(file);
  }

  function clearFileInput() {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  return {
    inputRef,
    attachment,
    isDragActive,
    isSaving,
    uploadIsActive,
    saveIsDisabled,
    handleClose,
    openFilePicker,
    handleUploadKeyDown,
    handleFileChange,
    handleDragStart,
    handleDragLeave,
    handleDrop,
    removeAttachment,
    handleSave
  };
}
